import { useEffect, useState } from "react";

import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { AttachedDocumentList } from "@/Common/Components/IconControls/AttachedDocumentList";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { FileType } from "@/Common/Models/FileType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { FaxDirectorySelectorComponent } from "@/ContactDirectory/Components/FaxDirectorySelectorComponent";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { FaxAndSignatureLabRequestSelection } from "@/ExportableFile/Components/FaxAndSignatureLabRequestSelection";
import { FaxCoverSheetEditorSection } from "@/ExportableFile/Components/FaxCoverSheetEditorSection";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileResponse } from "@/ExportableFile/Models/ExportableFileResponse";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";

interface IDocumentFaxProps {
    /** The callback method invoked when the component closes. */
    onClose: () => void;

    /** The callback method invoked when the documents must be faxed. */
    onFax: (selectedFiles: ExportableFile[], faxDetails: InboundFaxDetails, onCompleted?: VoidFunction) => Promise<void>;

    /** The patient linked to the document. */
    patient: Patient;

    /** The document selected by default. */
    selectedDocument: MedicalDocumentItemDto;
}

/**
 * The component that renders a view to send selected files through fax.
 * @param {IDocumentFaxProps} props The component properties.
 */
function DocumentFax(props: IDocumentFaxProps): JSX.Element {
    const { getText } = useTranslation();
    const [fileSelectionModalOpen, setFileSelectionModalOpen] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<ExportableFile[]>([]);
    const [exportableFilesModel, setExportableFilesModel] = useState<ExportableFileModel[]>([]);
    const [faxNumber, setFaxNumber] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [clinicianName, setClinicianName] = useState<string>("");
    const [canSendFax, setCanSendFax] = useState<boolean>(false);
    const [isFaxProcessing, setIsFaxProcessing] = useState<boolean>(false);
    const [isFaxNumberValid, setIsFaxNumberValid] = useState<boolean>(false);
    const [pdfCoverPageParams, setPdfCoverPageParams] = useState<IFaxCoverSheetProps>({});
    const [showCoverSheetValidationErrors, setShowCoverSheetValidationErrors] = useState<boolean>(false);
    const [isCoverSheetSubjectValid, setIsCoverSheetSubjectValid] = useState<boolean>(true);
    const [isCoverSheetMessageValid, setIsCoverSheetMessageValid] = useState<boolean>(true);

    useEffect(() => {
        setCanSendFax((!IsNullOrWhiteSpace(clinicianName)
            && selectedFiles.length > 0
            && isFaxNumberValid)
        );
    }, [clinicianName, selectedFiles, isFaxNumberValid]);

    useEffect(() => {
        if (props.selectedDocument.DocumentPathList.length > 0) {
            const updatedSelectedFiles = props.selectedDocument.DocumentPathList.map(file => ExportableFileHelper.fromExportableFileResponse(new ExportableFileResponse({
                Id: file.Id.toUpperCase(),
                Type: FileType.DOCUMENT,
                Date: file.CreationDate?.toString(),
                Description: file.OriginalFileName
            })));

            setSelectedFiles(updatedSelectedFiles);
        }
    }, [props.selectedDocument]);

    useEffect(() => {
        setExportableFilesModel(selectedFiles.map(file => file.toExportableFileModel()));
    }, [selectedFiles]);

    function handleSelectedFiles(selectedFiles: ExportableFile[]): void {
        setSelectedFiles(selectedFiles);
        if (fileSelectionModalOpen)
            setFileSelectionModalOpen(false);
    }

    function handleRemoveAttachment(_: number, file: ExportableFileModel): void {
        const filteredSelectedFiles = selectedFiles
            .filter(selectedFile => selectedFile.InternalId != file.Model.InternalId);

        handleSelectedFiles(filteredSelectedFiles);
    }

    function handleResolveDescription(attachment: ExportableFileModel): string {
        if (!IsNullOrWhiteSpace(attachment.displayDescription))
            return attachment.displayDescription;

        return FileSelectionHelper.determineTypeName(attachment.Model.fileType);
    }

    async function handleFaxSending(): Promise<void> {
        setIsFaxProcessing(true);

        if (pdfCoverPageParams?.GenerateCoverPage && (!isCoverSheetMessageValid || !isCoverSheetSubjectValid)) {
            setShowCoverSheetValidationErrors(true);
            setIsFaxProcessing(false);
            return;
        }

        const faxDetails: InboundFaxDetails = {
            displayName: clinicianName,
            faxNumber,
            pdfCoverPageParams,
            phoneNumber
        };

        await props.onFax(selectedFiles, faxDetails, () => setCanSendFax(true));
        setIsFaxProcessing(false);
    }

    function renderFooter(): JSX.Element {
        return (
            <div className="text-right">
                <button
                    className="btn btn-default push-right-5"
                    onClick={() => props.onClose()}
                >
                    {getText("CancelLabel")}
                </button>
                <button
                    className="btn btn-primary"
                    disabled={!canSendFax || isFaxProcessing}
                    onClick={() => handleFaxSending()}
                >
                    {getText("FaxVerbLabel")}
                </button>
            </div>
        );
    }

    return (
        <>
            <Modal
                title={getText("FaxDocumentsLabel")}
                onClose={() => props.onClose()}
                width={500}
                draggable
                disableOverlayClose={false}
                maxHeight="calc(100vh - 200px)"
                top={20}
                showOverlay
                showX
                footer={renderFooter()}
                disableAutoScroll={false}
                overlayZIndex={99}
            >
                <FeatureFlagControl
                    flag={cacheStore.ClinicSettings.EnableContactDirectory}
                    activeFeature={(
                        <FaxDirectorySelectorComponent
                            isFaxValid={isValid => setIsFaxNumberValid(isValid)}
                            onFaxUpdate={options => {
                                setFaxNumber(options.faxNumber);
                                setClinicianName(options.description);
                                setPhoneNumber(options.selectedEntity?.getRawPhoneNumber());
                            }}
                            patient={props.patient}
                        />
                    )}
                    fallbackFeature={(
                        <FaxAndSignatureLabRequestSelection
                            shouldFocusRecipientOnMount={!sessionStore.signOnPrintAccess}
                            onDataChange={(isValid, description, faxNumber, _exportableFile, phoneNumber) => {
                                setIsFaxNumberValid(isValid);
                                setFaxNumber(faxNumber);
                                setClinicianName(description);
                                setPhoneNumber(phoneNumber);
                            }}
                        />
                    )}
                />

                {cacheStore.ClinicSettings.EnableFaxCoverSheet && (
                    <FaxCoverSheetEditorSection
                        containerClassName="margin-sm-top"
                        subject={pdfCoverPageParams.CoverPageTitle}
                        message={pdfCoverPageParams.CoverPageContent}
                        isSubjectValid={showCoverSheetValidationErrors ? isCoverSheetSubjectValid : true}
                        isMessageValid={showCoverSheetValidationErrors ? isCoverSheetMessageValid : true}
                        validate={(isSubjectValid, isMessageValid) => {
                            setIsCoverSheetSubjectValid(isSubjectValid);
                            setIsCoverSheetMessageValid(isMessageValid);
                        }}
                        onCheckboxChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, GenerateCoverPage: value }));
                        }}
                        onSubjectChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, CoverPageTitle: value }));
                        }}
                        onMessageChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, CoverPageContent: value }));
                        }}
                    />
                )}

                <AttachDocumentLink onClick={() => setFileSelectionModalOpen(true)} />
                {selectedFiles.length > 0 && (
                    <div style={{ paddingLeft: "60px" }}>
                        <AttachedDocumentList
                            selectedAttachments={exportableFilesModel}
                            removeAttachmentFromList={(_, file) => handleRemoveAttachment(_, file)}
                            resolveDescription={attachment => handleResolveDescription(attachment)}
                        />
                    </div>
                )}
            </Modal>

            {fileSelectionModalOpen && (
                <FileSelectionContainer
                    patient={Person.FromPatient(props.patient)}
                    title={getText("AttachFilesLabel")}
                    type={FileSelectionContainerType.Attachment}
                    onClose={() => setFileSelectionModalOpen(false)}
                    onPrintOrAttachClicked={selected => handleSelectedFiles(selected)}
                    popupStyle={{ position: "fixed" }}
                    selectedFiles={selectedFiles}
                    autoManageOverlay
                />
            )}
        </>
    );
}

export { DocumentFax };
